import { repositoryFactory, DataSourceStrategy, CacheStrategy } from '@rammetec/data-access';
import { rtLogService } from '@rammetec/core';
import { PLAN } from '../types/plan.js';
import { lifecycleService } from './lifecycleService.js';
import { auditService } from './auditService.js';
import { expandPlan as expandPlanPure } from '../utils/moduleResolution.js';
import { normalizeTags } from '../utils/moduleTags.js';
import { stripUndefined } from './stripUndefined.js';
class PlanService {
    async getRepository() {
        return repositoryFactory.getRepository('plan', PLAN, DataSourceStrategy.Firestore, CacheStrategy.InMemory);
    }
    async getAllPlans() {
        try {
            const repository = await this.getRepository();
            return await repository.getAll();
        }
        catch (error) {
            rtLogService.error('Error fetching all plans', { error });
            return [];
        }
    }
    async getPlanById(id) {
        try {
            const repository = await this.getRepository();
            return await repository.getById(id);
        }
        catch (error) {
            rtLogService.error('Error fetching plan', { error, id });
            return null;
        }
    }
    async createPlan(data) {
        try {
            const repository = await this.getRepository();
            const inactivationDate = data.inactivationDate ?? null;
            const payload = stripUndefined({
                ...data,
                ...(data.tags != null ? { tags: normalizeTags(data.tags) } : {}),
                moduleIds: data.moduleIds ?? [],
                active: lifecycleService.isActive({ inactivationDate }),
                inactivationDate,
            });
            const created = await repository.create(auditService.stampOnCreate(payload));
            rtLogService.info('Plan created', { planId: created.id, name: data.name });
            return created;
        }
        catch (error) {
            rtLogService.error('Error creating plan', { error, name: data.name });
            throw error;
        }
    }
    async updatePlan(id, delta) {
        try {
            const repository = await this.getRepository();
            const withTags = delta.tags != null ? { ...delta, tags: normalizeTags(delta.tags) } : { ...delta };
            const withActive = 'inactivationDate' in withTags
                ? {
                    ...withTags,
                    active: lifecycleService.isActive({ inactivationDate: withTags.inactivationDate }),
                }
                : withTags;
            await repository.update(id, auditService.stampOnUpdate(stripUndefined(withActive)));
            rtLogService.info('Plan updated', { planId: id });
        }
        catch (error) {
            rtLogService.error('Error updating plan', { id, error });
            throw error;
        }
    }
    async deletePlan(id) {
        try {
            const repository = await this.getRepository();
            await repository.delete(id);
            rtLogService.info('Plan deleted', { planId: id });
        }
        catch (error) {
            rtLogService.error('Error deleting plan', { id, error });
            throw error;
        }
    }
    /**
     * Expands a plan into baseModuleIds: moduleIds ∪ modules matching any tag.
     * Pure over the provided catalog — used by entitlement sync and 0149 preview.
     */
    expandPlan(plan, catalog) {
        return expandPlanPure(plan, catalog);
    }
}
export const planService = new PlanService();
//# sourceMappingURL=planService.js.map